import every from "lodash/every";
import lodashInvert from "lodash/invert";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import isPlainObject from "lodash/isPlainObject";

type SelectDatum<T> = {
  value: T[keyof T];
  label: keyof T;
};

type SelectData<T> = SelectDatum<T>[];
type GenericRecord = Record<string, unknown>;

// Based on: https://stackoverflow.com/a/65285392
export function enumToSelectData<T extends object>(enumeration: T): SelectData<T> {
  return (Object.keys(enumeration) as Array<keyof T>).map((key) => ({
    value: enumeration[key],
    label: key,
  }));
}

// Based on: https://stackoverflow.com/a/64127710
export function invert<T extends Record<PropertyKey, PropertyKey>>(
  obj: T,
): {
  [K in keyof T as T[K]]: K;
} {
  return lodashInvert(obj) as any; // eslint-disable-line @typescript-eslint/no-explicit-any
}

export function updateNestedValue(values: GenericRecord, path: string[], updatedValue: unknown) {
  // Copy values to avoid mutating original object
  const updatedValues = { ...values };
  let currentDepth = updatedValues;

  for (let i = 0; i < path.length; i++) {
    const key = path[i];

    // Create missing path segments so "Add detail for problem" works on first click
    // before form initial values have been applied
    if (!(key in currentDepth) && i !== path.length - 1) {
      currentDepth[key] = {};
    }

    if (i === path.length - 1) {
      currentDepth[key] = updatedValue;
      continue;
    }

    if (!isPlainObject(currentDepth[key])) {
      break;
    }

    currentDepth[key] = { ...(currentDepth[key] as GenericRecord) };
    currentDepth = currentDepth[key] as GenericRecord;
  }

  return updatedValues;
}

function isEmptyValue(value: unknown): boolean {
  if (isNil(value)) {
    return true;
  }

  if (typeof value === "string" && value === "") {
    return true;
  }

  return false;
}

export function isAllKeysEmpty(obj: GenericRecord): boolean {
  if (isEmpty(obj)) {
    return true;
  }

  return every(obj, (value) => {
    if (isPlainObject(value) && !Array.isArray(value)) {
      return isAllKeysEmpty(value as GenericRecord);
    }
    return isEmptyValue(value);
  });
}
